import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { normalizeMarkdownInput } from '../../utils/markdownNormalize';

function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}

/** Spread {...props} first, then className — hast may pass className and must not override our styles. */
const bodyComponents = {
  p: ({ node, children, className, ...props }) => (
    <p {...props} className={cn('mb-3 text-ink last:mb-0', className)}>
      {children}
    </p>
  ),
  strong: ({ node, children, className, ...props }) => (
    <strong {...props} className={cn('!font-semibold !text-ink', className)}>
      {children}
    </strong>
  ),
  em: ({ node, children, className, ...props }) => (
    <em {...props} className={cn('!italic !text-ink', className)}>
      {children}
    </em>
  ),
  del: ({ node, children, className, ...props }) => (
    <del {...props} className={cn('!line-through !text-ink-muted', className)}>
      {children}
    </del>
  ),
  s: ({ node, children, className, ...props }) => (
    <s {...props} className={cn('!line-through !text-ink-muted', className)}>
      {children}
    </s>
  ),
  ul: ({ node, children, className, ...props }) => (
    <ul
      {...props}
      className={cn(
        'my-3 list-disc list-outside pl-5 text-ink marker:text-ink-subtle',
        'space-y-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_ul]:list-[circle] [&_ul_ul]:list-[square]',
        '[&_li>p]:mb-1 [&_li>p:last-child]:mb-0',
        className
      )}
    >
      {children}
    </ul>
  ),
  ol: ({ node, children, className, ...props }) => (
    <ol
      {...props}
      className={cn(
        'my-3 list-decimal list-outside pl-5 text-ink marker:text-ink-subtle',
        'space-y-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5',
        '[&_li>p]:mb-1 [&_li>p:last-child]:mb-0',
        className
      )}
    >
      {children}
    </ol>
  ),
  li: ({ node, children, className, ...props }) => (
    <li {...props} className={cn('leading-relaxed pl-0.5', className)}>
      {children}
    </li>
  ),
  /** GFM task lists: `- [ ]` / `- [x]` */
  input: ({ node, className, ...props }) => (
    <input
      {...props}
      className={cn(
        'mr-2 mt-0.5 inline-block h-4 w-4 shrink-0 cursor-default rounded border-edge-strong align-middle text-link',
        className
      )}
    />
  )
};

/**
 * Renders post body with GitHub-flavored markdown (bold/italic/strikethrough, line breaks).
 */
export default function PostBodyMarkdown({ children }) {
  if (children == null || children === '') return null;
  const source = normalizeMarkdownInput(typeof children === 'string' ? children : String(children));
  return (
    <div className="mb-6 max-w-none text-ink">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={bodyComponents}>
        {source}
      </ReactMarkdown>
    </div>
  );
}
